import React, { memo, useEffect, useRef } from 'react';
import { Button, ChatBubble, Textarea, Tooltip } from 'react-daisyui';
import { LuFileText, LuLoaderPinwheel, LuPlus, LuSend, LuTrash2 } from 'react-icons/lu';

import MarkdownRenderer from '../MarkdownRenderer';

export interface ChatMessage {
  sender: string;
  text: string;
  chatId: string;
  hidden?: boolean;
  context: {
    taskManager?: string;
    boardId?: string;
    projectKey?: string;
    docId?: string;
    filename?: string;
    [key: string]: any;
  };
  typing?: boolean;
  updatedAt?: number;
  createdAt?: number;
}

interface ChatUIProps {
  messages: ChatMessage[];
  typingText?: string;
  withJiraProjects?: boolean;
  projectSelectorLocked?: boolean;
  selectedJiraBoardId?: string;
  title?: string;
  typingSender?: string;
  newMessage: string;
  file: File | null;
  isFileLoading: boolean;
  onSend: () => void;
  onChange: (val: string) => void;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: () => void;
  onSelectProject?: (project: { id: string; name: string } | null) => void;
  senderId?: string;
}

const isMine = (msgSender: string, senderId?: string) =>
  msgSender === senderId || msgSender === 'VopTDzepO7OljHP2EoCSoJqkioB2';

const Message: React.FC<{ message: ChatMessage; mine: boolean }> = memo(({ message, mine }) => {
  // User bubble
  if (mine) {
    return (
      <ChatBubble end>
        <ChatBubble.Message className="whitespace-pre-wrap">{message.text}</ChatBubble.Message>

        <ChatBubble.Footer className="opacity-70">
          <div className="flex items-center gap-3 text-xs">
            {message.updatedAt && <span>{new Date(message.updatedAt).toLocaleString()}</span>}
            {message.context?.filename && (
              <span className="inline-flex items-center gap-1">
                <LuFileText />
                <span className="truncate max-w-[220px]">{message.context.filename}</span>
              </span>
            )}
          </div>
        </ChatBubble.Footer>
      </ChatBubble>
    );
  }

  // Assistant message (markdown)
  if (!message.hidden) {
    return (
      <div className="max-w-3xl">
        <div className="bg-base-100 rounded-xl p-4 shadow-card">
          <MarkdownRenderer markdown={message.text} />
        </div>
        {message.updatedAt && (
          <div className="text-xs opacity-60 mt-1 ml-2">{new Date(message.updatedAt).toLocaleString()}</div>
        )}
      </div>
    );
  }

  return null;
});

Message.displayName = 'Message';

export const ChatUI: React.FC<ChatUIProps> = ({
  messages,
  title,
  typingText,
  typingSender,
  newMessage,
  file,
  isFileLoading,
  onSend,
  onChange,
  onFileUpload,
  onRemoveFile,
  senderId,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // auto-grow textarea
  useEffect(() => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = 'auto';
    textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
  }, [newMessage]);

  const headerTitle = title || 'Chat';

  return (
    <div className="h-screen bg-base-200 flex flex-col">
      {/* Header (sticky) */}
      <div className="sticky top-0 z-10 bg-base-200/80 backdrop-blur border-b border-base-300">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-start md:items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-lg font-semibold truncate">{headerTitle}</div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-4 py-4 space-y-3">
          {messages?.map((msg, index) => (
            <Message key={msg.chatId + index} message={msg} mine={isMine(msg.sender, senderId)} />
          ))}

          {typingText && typingSender && (
            <div className="max-w-3xl">
              <div className="bg-base-100 rounded-xl p-3 shadow-card inline-flex items-center gap-2">
                <LuLoaderPinwheel className="animate-spin opacity-70" size={18} />
                <span className="text-sm opacity-80">{typingText}</span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Composer (sticky) */}
      <div className="sticky bottom-0 bg-base-200/80 backdrop-blur border-t border-base-300">
        <div className="max-w-5xl mx-auto px-4 py-3">
          {/* Attached file */}
          {(file || isFileLoading) && (
            <div className="mb-2">
              <div className="inline-flex items-center gap-2 bg-base-100 rounded-lg px-3 py-2 shadow-card">
                {isFileLoading ? (
                  <LuLoaderPinwheel className="animate-spin opacity-70" />
                ) : (
                  <LuFileText className="opacity-70" />
                )}
                <span className="text-sm truncate max-w-[420px]">{file?.name}</span>

                {!isFileLoading && (
                  <button className="btn btn-ghost btn-xs" onClick={onRemoveFile} title="Remove file">
                    <LuTrash2 />
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="bg-base-100 rounded-xl shadow-card">
            <div className="flex items-end gap-2 p-2">
              {/* left actions */}
              <div className="flex items-center gap-1 pb-1">
                {!file && (
                  <Tooltip message="Attach a file (.pdf, .doc, .docx, .md, .txt)">
                    <label className="btn btn-ghost btn-sm btn-circle">
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.md,.markdown,.txt"
                        onChange={onFileUpload}
                        className="hidden"
                      />
                      <LuPlus className="text-lg" />
                    </label>
                  </Tooltip>
                )}
              </div>

              {/* textarea */}
              <div className="flex-1">
                <Textarea
                  ref={textareaRef}
                  value={newMessage}
                  onChange={(e) => onChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      onSend();
                    }
                  }}
                  placeholder="Type a message…"
                  bordered={false}
                  className="w-full bg-transparent resize-none overflow-hidden focus:outline-none text-base"
                  rows={1}
                />
                <div className="text-[11px] opacity-50 px-1 pb-1">Enter — send • Shift+Enter — new line</div>
              </div>

              {/* send */}
              <Tooltip message={isFileLoading ? 'File is processing…' : 'Send message'}>
                <Button
                  onClick={onSend}
                  disabled={isFileLoading}
                  className="btn btn-primary btn-sm btn-circle"
                  startIcon={<LuSend className="text-lg" />}
                />
              </Tooltip>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
